/**
 * ZNAK MARKE (Z15): serifno "S" s vodoravnim potezom kroz donju polovicu.
 *
 * **Zašto crtež, a ne slovo u fontu.** Dosadašnja ikona bila je `<text>S</text>`
 * u Georgiji: na uređaju bez te obitelji preglednik uzme što ima, pa je ikona
 * aplikacije izgledala drukčije na svakom drugom telefonu — a ikona je ono po
 * čemu se aplikacija nalazi na početnom zaslonu. Kad ju treba i rasterizirati
 * (PNG veličine za manifest), font postaje ovisnost koje na poslužitelju za
 * izradu ikona uopće nema.
 *
 * **Zašto se računa, a ne piše kao gotov `d`.** Slovo je modulirani potez:
 * tanko na krajevima, najdeblje u dijagonali — to je jedno pravilo (`sirina`),
 * a ne dvjestotinjak brojeva koje nitko ne bi znao ispraviti. Ovdje stoji
 * kičma (`KICMA`) i debljina po njoj; obris je posljedica. Isti račun crta
 * `components/Znak.tsx` u sučelju i `tools/ikone.ts` u datoteke, pa se ikona i
 * zaglavlje ne mogu raziću.
 *
 * Boje NE stoje ovdje: putanje se boje kroz `currentColor` (§17 — tokeni, ne
 * heksadekadske vrijednosti po komponenti), a datoteke ikona nose paletu marke.
 */

export const VIEWBOX = '0 0 512 512'

/** Kubične krivulje kičme slova, od gornjeg desnog kraja do donjeg lijevog. */
const KICMA: [Tocka, Tocka, Tocka, Tocka][] = [
  [
    { x: 336, y: 168 },
    { x: 322, y: 126 },
    { x: 272, y: 108 },
    { x: 222, y: 114 },
  ],
  [
    { x: 222, y: 114 },
    { x: 176, y: 120 },
    { x: 154, y: 152 },
    { x: 164, y: 188 },
  ],
  [
    { x: 164, y: 188 },
    { x: 174, y: 224 },
    { x: 216, y: 240 },
    { x: 256, y: 256 },
  ],
  [
    { x: 256, y: 256 },
    { x: 300, y: 274 },
    { x: 344, y: 292 },
    { x: 348, y: 328 },
  ],
  [
    { x: 348, y: 328 },
    { x: 354, y: 366 },
    { x: 330, y: 394 },
    { x: 286, y: 400 },
  ],
  [
    { x: 286, y: 400 },
    { x: 238, y: 406 },
    { x: 194, y: 388 },
    { x: 176, y: 352 },
  ],
]

/** Najtanje mjesto poteza (uz krajeve) i najdeblje (dijagonala). */
const TANKO = 17
const DEBELO = 52

/** Krajevi se blago rašire — serifni završetak, ne odrezana cijev. */
const KAP = 7
const DOSEG_KAPI = 0.07

/** Koliko se točaka uzima po kičmi; iznad toga se razlika više ne vidi. */
const UZORAKA = 240

interface Tocka {
  x: number
  y: number
}

/**
 * Debljina poteza na položaju `u` (0 = gornji kraj, 1 = donji).
 *
 * Sinus daje moduliranu os (tanko gore i dolje, najdeblje u sredini), a
 * eksponencijalni dodaci na krajevima serifni završetak: bez njih potez izgleda
 * kao odrezana cijev, s njima kao pisano slovo.
 */
function sirina(u: number): number {
  const os = TANKO + (DEBELO - TANKO) * Math.sin(Math.PI * u) ** 0.85
  const kraj = KAP * (Math.exp(-u / DOSEG_KAPI) + Math.exp(-(1 - u) / DOSEG_KAPI))

  return os + kraj
}

function naKrivulji(
  [a, b, c, d]: [Tocka, Tocka, Tocka, Tocka],
  t: number,
): { tocka: Tocka; smjer: Tocka } {
  const m = 1 - t

  const tocka = {
    x: m ** 3 * a.x + 3 * m ** 2 * t * b.x + 3 * m * t ** 2 * c.x + t ** 3 * d.x,
    y: m ** 3 * a.y + 3 * m ** 2 * t * b.y + 3 * m * t ** 2 * c.y + t ** 3 * d.y,
  }

  const smjer = {
    x: 3 * m ** 2 * (b.x - a.x) + 6 * m * t * (c.x - b.x) + 3 * t ** 2 * (d.x - c.x),
    y: 3 * m ** 2 * (b.y - a.y) + 6 * m * t * (c.y - b.y) + 3 * t ** 2 * (d.y - c.y),
  }

  return { tocka, smjer }
}

/** Obris slova: lijeva strana poteza, kapa, desna strana unatrag, kapa natrag. */
export function putanjaS(): string {
  const lijevo: Tocka[] = []
  const desno: Tocka[] = []

  for (let i = 0; i < UZORAKA; i++) {
    const u = i / (UZORAKA - 1)

    // Ravnomjerno po SEGMENTIMA, ne po duljini luka: segmenti su namjerno
    // slične duljine, pa je razlika ispod piksela, a račun ostaje čitljiv.
    const polozaj = Math.min(u * KICMA.length, KICMA.length - 1e-9)
    const segment = KICMA[Math.floor(polozaj)]!
    const { tocka, smjer } = naKrivulji(segment, polozaj - Math.floor(polozaj))

    const duljina = Math.hypot(smjer.x, smjer.y) || 1
    const normala = { x: -smjer.y / duljina, y: smjer.x / duljina }
    const pola = sirina(u) / 2

    lijevo.push({ x: tocka.x + normala.x * pola, y: tocka.y + normala.y * pola })
    desno.push({ x: tocka.x - normala.x * pola, y: tocka.y - normala.y * pola })
  }

  const obris = [
    ...lijevo,
    ...kapa(lijevo[lijevo.length - 1]!, desno[desno.length - 1]!),
    ...desno.slice().reverse(),
    ...kapa(desno[0]!, lijevo[0]!),
  ]

  return uPutanju(obris)
}

/** Polukružni završetak između dvije strane poteza. */
function kapa(od: Tocka, do_: Tocka): Tocka[] {
  const srediste = { x: (od.x + do_.x) / 2, y: (od.y + do_.y) / 2 }
  const polumjer = Math.hypot(od.x - do_.x, od.y - do_.y) / 2
  const pocetak = Math.atan2(od.y - srediste.y, od.x - srediste.x)

  return Array.from({ length: 11 }, (_, i) => {
    const kut = pocetak - Math.PI * ((i + 1) / 12)

    return { x: srediste.x + Math.cos(kut) * polumjer, y: srediste.y + Math.sin(kut) * polumjer }
  })
}

/**
 * Vodoravni potez kroz donju polovicu — dio znaka, ne ukras preko njega.
 *
 * Proteže se izvan slova na obje strane (zato je znak prepoznatljiv i na 16
 * piksela favicona, gdje se oblina slova izgubi), a krajevi su zaobljeni istim
 * polumjerom kojim i potez slova završava.
 */
export function putanjaPoteza(): string {
  const od = 92
  const do_ = 420
  const y = 336
  const pola = 8

  const luk = (sredisteX: number, smjer: 1 | -1) =>
    Array.from({ length: 13 }, (_, i) => {
      const kut = (-Math.PI / 2) * smjer + smjer * Math.PI * (i / 12)

      return { x: sredisteX + Math.cos(kut) * pola * smjer, y: y + Math.sin(kut) * pola }
    })

  return uPutanju([...luk(do_ - pola, 1), ...luk(od + pola, -1)])
}

function uPutanju(tocke: Tocka[]): string {
  return (
    tocke
      .map((tocka, i) => `${i === 0 ? 'M' : 'L'}${zaokruzi(tocka.x)} ${zaokruzi(tocka.y)}`)
      .join('') + 'Z'
  )
}

function zaokruzi(broj: number): string {
  return (Math.round(broj * 100) / 100).toString()
}
